/**
 * @module builder
 */
import type { TieredPackages, TieredPackageTier, TieredPackageTierId } from 'paintscout'
import {
  BASE_TIER_ID,
  getTieredPackageContentType,
  getTieredPackageFeatures,
  getTieredPackageSetColor,
  TIER_ORDER
} from '../../quote/tiered-packages'
import type { TieredPackagePresetOption } from '../index'
import { isProductSwapEnabled } from './is-product-swap-enabled'

/**
 * A template carries no prices — it has no estimate behind it — so every tier lands on the quote
 * unpriced. Basic and Premium then cost what the estimate costs until either the template's product
 * swap reprices them or the estimator overrides them in the drawer.
 */
function unpricedTiers(preset: TieredPackagePresetOption): Record<TieredPackageTierId, TieredPackageTier> {
  return TIER_ORDER.reduce(
    (acc, tierId) => ({
      ...acc,
      [tierId]: {
        ...preset.tiers[tierId],
        adjustment: { type: 'percentage' as const, value: 0 },
        overridden: undefined,
        applyToOptions: undefined,
        resolvedPrice: undefined
      }
    }),
    {} as Record<TieredPackageTierId, TieredPackageTier>
  )
}

/**
 * Map a saved preset into a quote-ready TieredPackages config. The selected tier starts on the base
 * so the quote total is unchanged until the customer/estimator picks another tier.
 */
export function tieredPackagesFromPreset(args: { preset: TieredPackagePresetOption }): TieredPackages {
  const { preset } = args

  return {
    enabled: true,
    // The estimate as built is always the middle package, whatever an older template stored.
    baseTierId: BASE_TIER_ID,
    selectedTierId: BASE_TIER_ID,
    recommendedTierId: preset.recommendedTierId,
    priceMode: 'percentage',
    priceDisplay: preset.priceDisplay,
    // Resolved, not copied: an older preset carries these on its tiers, and the quote should end up
    // with the set-level values either way.
    color: getTieredPackageSetColor(preset),
    contentType: getTieredPackageContentType(preset),
    tiers: unpricedTiers(preset),
    // Presentation carries over so the quote's cards look like the template's preview.
    features: getTieredPackageFeatures(preset),
    // Copied, not referenced: an estimate can then diverge from the company template, and the
    // customer view can read the rules without loading Settings. A template with swapping switched
    // off keeps its rules for later, so they are dropped here rather than carried onto the quote.
    productRules: isProductSwapEnabled(preset) ? preset.productRules ?? [] : [],
    font: preset.font,
    layout: preset.layout,
    badgeLabel: preset.badgeLabel,
    // Copied so the sidebar card can name the template without loading Settings, and so a renamed
    // or deleted template does not leave the estimate nameless.
    presetKey: preset.key,
    presetLabel: preset.label
  }
}
